import * as THREE from 'three';
import { createViewer, hasWebGL } from './viewer.ts';
import { createAtlas } from './textures.ts';
import { createPsxMaterial } from './psx.ts';
import { createStation, HALF_X, TRACK_Z } from './scene/station.ts';
import { createWallCuller } from './scene/walls.ts';
import { createTrain } from './scene/train.ts';
import { createTrainLoop } from './scene/animation.ts';
import { installLockdown, onSecretGesture } from './lockdown.ts';
import { installUpdater } from './update.ts';
import { buildPanel, hideLoading, showFatal, showLoading } from './ui.ts';

export function boot() {
  installLockdown();
  installUpdater();

  const container = document.getElementById('app');
  if (!container) throw new Error('missing #app');

  if (!hasWebGL()) {
    showFatal('3D is not available on this device.');
    return;
  }

  showLoading();

  const viewer = createViewer(container);
  const atlas = createAtlas();
  const material = createPsxMaterial(atlas.texture, {
    // Styling flags, off by default. The low render resolution is doing the
    // real work; these are available from the hidden panel.
    jitter: false,
    affine: false,
  });

  // The trains need their own material instance so they can be clipped at the
  // tunnel mouths; the station must not be, or its end walls would vanish.
  const trainMaterial = createPsxMaterial(atlas.texture, { clipX: HALF_X });

  const diorama = new THREE.Group();
  const station = createStation(atlas, material);

  // One train per track, running opposite ways. The tracks are in the middle and
  // the platforms are outboard, so each train's doors face away from the centre.
  const trains = TRACK_Z.map((trackZ) =>
    createTrain(atlas, trainMaterial, {
      trackZ,
      doorsFacing: trackZ < 0 ? -1 : 1,
    })
  );

  diorama.add(station.group, ...trains.map((t) => t.group));
  viewer.scene.add(diorama);

  // Frame the station only: the train slides well outside the room, and framing
  // that would leave the diorama tiny in the middle of an empty view.
  const radius = viewer.frame(station.group);
  // The camera sits two radii back, so the scene occupies depths of roughly one
  // to three radii. Start the haze past the middle and finish it just beyond the
  // far corner, which is exactly where the tunnel mouths are.
  material.setFog(radius * 2.4, radius * 3.4);
  trainMaterial.setFog(radius * 2.4, radius * 3.4);

  const cullWalls = createWallCuller(station.walls, viewer.camera);
  const loops = trains.map((train, i) =>
    createTrainLoop(train, viewer, {
      // The -Z track runs one way, the +Z track the other.
      direction: i === 0 ? 1 : -1,
      // Offset so the two are never in lockstep, which would read as a
      // mechanism rather than as a station.
      startDelay: i === 0 ? 0 : 9,
      reason: `train${i}`,
    })
  );

  viewer.onFrame((dt) => {
    cullWalls();
    for (const loop of loops) loop.update(dt);
  });
  cullWalls();

  const panel = buildPanel({
    onResetView: () => viewer.resetView(),
    onToggleFlag: (flag, on) => {
      material.setFlag(flag, on);
      trainMaterial.setFlag(flag, on);
      viewer.invalidate();
    },
    stats: () => viewer.stats(),
  });
  onSecretGesture(panel.open);

  if (import.meta.env.DEV) {
    // Dev-only. Vite replaces import.meta.env.DEV with false for production, so
    // this whole block is dead code the bundler drops - including the dynamic
    // import, so lil-gui never reaches the shipped bundle.
    (globalThis as Record<string, unknown>).__diorama = {
      viewer,
      station,
      material,
      trainMaterial,
      cullWalls,
      trains,
      loops,
    };
    import('./dev/gui.ts')
      .then(({ installDevGui }) =>
        installDevGui({ viewer, loops, materials: [material, trainMaterial] })
      )
      .catch((error) => console.warn('dev gui unavailable', error));
  }

  hideLoading();
}

function reportFatal(error: unknown) {
  console.error(error);
  showFatal('Something went wrong.');
}

try {
  boot();
} catch (error) {
  reportFatal(error);
}
